// Тип бизнеса клиента: Парк, Мониторинг и CRM, Мастерская или всё сразу.
//
// Тип задаёт, какие разделы человек видит, и от него зависят нижние вкладки на
// телефоне. Источник - capabilities.business_profile, когда бэкенд его отдаёт.
// Пока его нет, тип выводится из набора модулей тарифа: так меню и подсказки
// работают уже сейчас, а после появления поля на бэкенде берётся оно.
export const PROFILE_NAMES = {
  PARK: 'Парк',
  MONITORING_CRM: 'Мониторинг и CRM',
  WORKSHOP: 'Мастерская',
  ALL: 'Парк и мастерская',
}

const has = (modules, ...codes) => codes.some((code) => modules.includes(code))

export const resolveBusinessProfile = (capabilities) => {
  const given = capabilities?.business_profile
  if (given?.code) {
    return { code: given.code, name: given.name || PROFILE_NAMES[given.code] || given.code }
  }

  const modules = capabilities?.modules
  if (!Array.isArray(modules)) return null

  const fleet = has(modules, 'rents', 'couriers')
  const monitoring = has(modules, 'monitoring')
  const workshop = has(modules, 'repairs', 'warehouse')

  // Это догадка по набору модулей, пока бэкенд не отдаёт business_profile.
  // «Парк без мастерской» и «Мониторинг + CRM» по модулям не различаются, и
  // называть такую компанию «Парком» значило бы угадывать, поэтому для неё типа
  // нет, пока он не придёт с бэкенда
  let code = null
  if (fleet && workshop) code = 'ALL'
  else if (workshop && monitoring) code = 'ALL'
  else if (workshop) code = 'WORKSHOP'
  else if (monitoring && !fleet) code = 'MONITORING_CRM'

  return code ? { code, name: PROFILE_NAMES[code] } : null
}

// Что в SmartPark есть, а у клиента не подключено. Не больше двух: цель -
// дать понять, что есть куда расти, а не завалить списком модулей
export const missingOffers = (capabilities) => {
  // Бэкенд сам знает, какие модули есть в каталоге и чего у компании нет, и
  // отдаёт названия и описания: список меняется без релиза фронта
  const offered = capabilities?.modules_offered
  if (Array.isArray(offered)) {
    return offered
      .filter((item) => item.name)
      .map((item) => ({ key: item.code, title: item.name, text: item.description || '' }))
      .slice(0, 3)
  }

  // ВРЕМЕННО: запасные тексты, пока бэкенд не отдаёт modules_offered. Как только
  // поле поедет, весь блок ниже удалить: список модулей должен меняться без
  // релиза фронта, а тексты здесь именно это ломают
  const modules = capabilities?.modules
  if (!Array.isArray(modules)) return []

  const offers = []
  if (!has(modules, 'monitoring')) {
    offers.push({
      key: 'fleet',
      title: 'Сдаёте байки в аренду?',
      text: 'В SmartPark есть мониторинг парка, аренды и реестр курьеров.',
    })
  } else if (!has(modules, 'rents', 'couriers')) {
    offers.push({
      key: 'rents',
      title: 'Аренды и курьеры',
      text: 'Бронирования, приёмка и возврат, реестр курьеров.',
    })
  }
  if (!has(modules, 'repairs')) {
    offers.push({
      key: 'workshop',
      title: 'Есть своя мастерская?',
      text: 'Заказ-наряды, прайс услуг, задачи мастеров и склад запчастей.',
    })
  }
  return offers.slice(0, 2)
}

// Какие разделы вынести во вкладки внизу на телефоне. Сначала по роли: механику
// нужны задачи и наряды, диспетчеру - аренды и курьеры. Роль без своего списка
// (владелец, оператор) берёт список по типу бизнеса.
const BY_ROLE = {
  mechanic: ['/tasks', '/work-orders', '/warehouse', '/service-price'],
  storekeeper: ['/warehouse', '/work-orders', '/tasks', '/service-price'],
  dispatcher: ['/rents', '/drivers', '/tasks', '/fleet'],
  park_manager: ['/fleet', '/rents', '/drivers', '/tasks'],
  viewer: ['/dashboard', '/fleet', '/rents', '/tasks'],
}

const BY_PROFILE = {
  PARK: ['/fleet', '/rents', '/drivers', '/tasks'],
  MONITORING_CRM: ['/fleet', '/drivers', '/payments', '/dashboard'],
  WORKSHOP: ['/work-orders', '/tasks', '/warehouse', '/service-price'],
  ALL: ['/fleet', '/rents', '/tasks', '/work-orders'],
}

export const tabPriority = (roleCode, profileCode) =>
  BY_ROLE[roleCode] || BY_PROFILE[profileCode] || BY_PROFILE.PARK
